import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { 
  SwitchCamera, 
  Sparkles, 
  Sun, 
  Moon,
  Contrast,
  Palette,
  Loader2,
  X,
  Check,
  Smartphone,
  Monitor,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

/**
 * CSS-based video filters for live streams
 * These work across all browsers and devices without WebGL
 */
const VIDEO_FILTERS = [
  {
    id: 'none',
    name: 'No Filter',
    icon: X,
    filter: 'none',
    description: 'Original video'
  },
  {
    id: 'soft-glow',
    name: 'Soft Glow',
    icon: Sun,
    filter: 'brightness(1.1) contrast(0.95) saturate(1.1)',
    description: 'Warm, soft lighting'
  },
  {
    id: 'high-contrast',
    name: 'High Contrast',
    icon: Contrast,
    filter: 'contrast(1.3) saturate(1.1)',
    description: 'Bold and crisp'
  },
  {
    id: 'black-white',
    name: 'B&W',
    icon: Moon,
    filter: 'grayscale(1) contrast(1.1)',
    description: 'Classic monochrome'
  },
  {
    id: 'warm-tone',
    name: 'Warm',
    icon: Sun,
    filter: 'sepia(0.3) saturate(1.2) brightness(1.05)',
    description: 'Golden warm tones'
  },
  {
    id: 'cool-tone',
    name: 'Cool',
    icon: Moon,
    filter: 'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
    description: 'Blue cool tones'
  },
  {
    id: 'vivid',
    name: 'Vivid',
    icon: Palette,
    filter: 'saturate(1.5) contrast(1.1)',
    description: 'Enhanced colors'
  },
  {
    id: 'cinematic',
    name: 'Cinematic',
    icon: Sparkles,
    filter: 'contrast(1.15) saturate(0.85) brightness(0.95)',
    description: 'Film-like look'
  }
];

/**
 * LiveStreamControls - Camera flip and filter controls for live streaming
 * 
 * Props:
 * - onFilterChange: (filter: string) => void - Called when filter changes
 * - onCameraSwitch: (deviceId: string) => void - Called when camera switches
 * - disabled: boolean - Disable all controls
 * - className: string - Additional classes
 */
export function LiveStreamControls({ 
  onFilterChange, 
  onCameraSwitch, 
  disabled = false,
  className 
}) {
  const [cameras, setCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [currentFilter, setCurrentFilter] = useState('none');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimeoutRef = useRef(null);

  // Detect available cameras
  useEffect(() => {
    detectCameras();
  }, []);

  // Auto-hide controls after 3 seconds
  useEffect(() => {
    if (showControls) {
      hideTimeoutRef.current = setTimeout(() => {
        if (!isFiltersOpen) {
          setShowControls(false);
        }
      }, 3000);
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [showControls, isFiltersOpen]);

  const detectCameras = async () => {
    try {
      // Request permission first
      await navigator.mediaDevices.getUserMedia({ video: true });
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      setCameras(videoDevices);
      console.log('[LiveStreamControls] Detected cameras:', videoDevices.length);
    } catch (error) {
      console.error('[LiveStreamControls] Camera detection error:', error);
      toast.error('Could not access camera');
    }
  };

  const handleCameraSwitch = async () => {
    if (cameras.length < 2 || isSwitching) return;

    setIsSwitching(true);
    
    try {
      const nextIndex = (currentCameraIndex + 1) % cameras.length;
      const nextCamera = cameras[nextIndex];
      
      console.log('[LiveStreamControls] Switching to camera:', nextCamera.label);
      
      if (onCameraSwitch) {
        await onCameraSwitch(nextCamera.deviceId);
      }
      
      setCurrentCameraIndex(nextIndex);
      
      // Haptic feedback on mobile
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      toast.success(`Switched to ${nextCamera.label || `Camera ${nextIndex + 1}`}`);
    } catch (error) {
      console.error('[LiveStreamControls] Camera switch error:', error);
      toast.error('Failed to switch camera');
    } finally {
      setIsSwitching(false);
    }
  };

  const handleFilterChange = (filterId) => {
    const filter = VIDEO_FILTERS.find(f => f.id === filterId);
    if (!filter) return;

    setCurrentFilter(filterId);
    
    if (onFilterChange) {
      onFilterChange(filter.filter);
    }
    
    // Haptic feedback on mobile
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
    
    console.log('[LiveStreamControls] Applied filter:', filterId);
  };

  const handleShowControls = () => {
    setShowControls(true);
  };

  const currentFilterData = VIDEO_FILTERS.find(f => f.id === currentFilter) || VIDEO_FILTERS[0];
  const hasMultipleCameras = cameras.length > 1;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <>
      {/* Tap area to show controls when hidden - avoid blocking side panel */}
      {!showControls && (
        <div 
          className="fixed inset-0 z-30 lg:right-64"
          onClick={handleShowControls}
          data-testid="controls-tap-area"
        />
      )}

      {/* Floating controls */}
      <div 
        className={cn(
          "fixed bottom-24 right-4 z-50 flex flex-col gap-2 transition-all duration-300",
          showControls ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none",
          className
        )}
        data-testid="live-stream-controls"
      >
        {/* Flip Camera Button */}
        {hasMultipleCameras && (
          <Button
            variant="secondary"
            size="icon"
            className="w-12 h-12 rounded-full bg-background/70 hover:bg-background/90 backdrop-blur-sm"
            onClick={handleCameraSwitch}
            disabled={disabled || isSwitching}
            data-testid="flip-camera-btn"
          >
            {isSwitching ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <SwitchCamera className="w-5 h-5" />
            )}
          </Button>
        )}

        {/* Filters Button */}
        <Popover open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              className={cn(
                "w-12 h-12 rounded-full bg-background/70 hover:bg-background/90 backdrop-blur-sm",
                currentFilter !== 'none' && "ring-2 ring-secondary"
              )}
              disabled={disabled}
              data-testid="filters-btn"
            >
              <Sparkles className={cn(
                "w-5 h-5",
                currentFilter !== 'none' && "text-secondary"
              )} />
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            side="left" 
            align="end"
            className="w-64 p-2 bg-background/90 backdrop-blur-md border-border"
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between px-2 pb-2 border-b border-border">
                <span className="text-sm font-medium">Filters</span>
                {currentFilter !== 'none' && (
                  <Badge variant="outline" className="text-xs">
                    {currentFilterData.name}
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-4 gap-1">
                {VIDEO_FILTERS.map((filter) => {
                  const Icon = filter.icon;
                  const isActive = currentFilter === filter.id;
                  
                  return (
                    <button
                      key={filter.id}
                      onClick={() => handleFilterChange(filter.id)}
                      className={cn(
                        "flex flex-col items-center justify-center p-2 rounded-lg transition-all",
                        isActive 
                          ? "bg-secondary/20 ring-1 ring-secondary" 
                          : "hover:bg-muted/50"
                      )}
                      title={filter.description}
                      data-testid={`filter-${filter.id}`}
                    >
                      <Icon className={cn(
                        "w-5 h-5 mb-1",
                        isActive ? "text-secondary" : "text-muted-foreground"
                      )} />
                      <span className={cn(
                        "text-[10px] truncate max-w-full",
                        isActive ? "text-secondary" : "text-muted-foreground"
                      )}>
                        {filter.name}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Device info */}
              <div className="flex items-center gap-2 px-2 pt-2 border-t border-border text-xs text-muted-foreground">
                {isMobile ? (
                  <Smartphone className="w-3 h-3" />
                ) : (
                  <Monitor className="w-3 h-3" />
                )}
                <span>
                  {cameras.length} camera{cameras.length !== 1 ? 's' : ''} detected
                </span>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Current filter indicator (when not in popover) */}
        {currentFilter !== 'none' && !isFiltersOpen && (
          <Badge 
            variant="secondary" 
            className="absolute -top-2 -left-2 text-xs px-1.5 py-0.5"
          >
            {currentFilterData.name}
          </Badge>
        )}
      </div>

      {/* Instructions overlay (shown once) */}
      {showControls && cameras.length === 1 && (
        <div className="fixed bottom-24 left-4 z-50 max-w-[200px] text-xs text-muted-foreground bg-background/50 backdrop-blur-sm rounded-lg p-2">
          <AlertCircle className="w-3 h-3 inline mr-1" />
          Only 1 camera detected. Connect another to enable flip.
        </div>
      )}
    </>
  );
}

/**
 * VideoFilterOverlay - Applies CSS filter to video element
 * Wrap your video element with this component
 */
export function VideoFilterOverlay({ filter = 'none', children, className }) {
  return (
    <div 
      className={cn("relative", className)}
      style={{ filter }}
      data-testid="video-filter-overlay"
    >
      {children}
    </div>
  );
}

/**
 * Hook to manage video filter state
 */
export function useVideoFilter() {
  const [filter, setFilter] = useState('none');
  
  const applyFilter = useCallback((filterId) => {
    const filterData = VIDEO_FILTERS.find(f => f.id === filterId);
    if (filterData) {
      setFilter(filterData.filter);
    }
  }, []);
  
  const clearFilter = useCallback(() => {
    setFilter('none');
  }, []);
  
  return {
    filter,
    applyFilter,
    clearFilter,
    setFilter
  };
}

/**
 * Hook to manage camera switching
 */
export function useCameraSwitch() {
  const [cameras, setCameras] = useState([]);
  const [currentCamera, setCurrentCamera] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    detectCameras();
  }, []);

  const detectCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setCameras(videoDevices);
      if (videoDevices.length > 0 && !currentCamera) {
        setCurrentCamera(videoDevices[0]);
      }
    } catch (error) {
      console.error('Camera detection error:', error);
    }
  };

  const switchCamera = useCallback(async () => {
    if (cameras.length < 2) return null;
    
    setIsLoading(true);
    try {
      const currentIndex = cameras.findIndex(c => c.deviceId === currentCamera?.deviceId);
      const nextIndex = (currentIndex + 1) % cameras.length;
      const nextCamera = cameras[nextIndex];
      
      setCurrentCamera(nextCamera);
      return nextCamera;
    } finally {
      setIsLoading(false);
    }
  }, [cameras, currentCamera]);

  return {
    cameras,
    currentCamera,
    switchCamera,
    isLoading,
    hasMultipleCameras: cameras.length > 1
  };
}

export default LiveStreamControls;
